// Shared language contract for the storefront. Intentionally NOT imported from
// the services `@local/locale` package: different repo, different concern. We
// share the CONVENTION (same `["en","de"]` union), not the code (per plan §M3).

// Adding a code here is one step of many, and the compiler only catches some of
// them: it flags every `Record<Language, T>` (the ~43 catalogs, the native
// language names, each static page's registry) and stays silent on every
// `language === "de"` branch, starting with `bcp47` below. Runbook + the full
// file-by-file checklist: `services` repo, `docs/i18n/add-a-language.md` and
// `docs/i18n/language-change-surface.md`. Keep both updated in the same PR when
// you add a language-dependent branch anywhere in this repo (DEV-10050).
export const SUPPORTED_LANGUAGES = ["en", "de"] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: Language = "en"

const SUPPORTED_SET = new Set<string>(SUPPORTED_LANGUAGES)

const isLanguage = (value: string): value is Language =>
  SUPPORTED_SET.has(value)

/**
 * Coerce an arbitrary locale/language input to a supported {@link Language},
 * falling back to `en`. Accepts a bare code (`"de"`) or a full locale tag
 * (`"de-DE"`). Only the primary subtag is considered, lower-cased.
 */
export const languageOf = (input: string | null | undefined): Language => {
  if (!input) return DEFAULT_LANGUAGE
  const primary = input.trim().toLowerCase().split(/[-_]/)[0]
  return isLanguage(primary) ? primary : DEFAULT_LANGUAGE
}

/**
 * DEV-9850: public market-language variants.
 *
 * The market↔language relation is many-to-many (spec: `language_in_market`
 * in the offer DB, every market's set includes "en", default via
 * `markets.default_language`). M5 ships this registry as the hardcoded
 * code-level projection of that relation; DEV-9851 swaps it to data.
 * Registry shape mirrors the schema on purpose.
 */
export interface MarketLanguageVariant {
  market: string
  language: Language
  /** Explicit hreflang token. Derived codes are unsafe (see en-xx warts). */
  hreflang: string
  /** Human label for the market/region selector. */
  label: string
}

export const MARKET_LANGUAGE_VARIANTS: readonly MarketLanguageVariant[] = [
  {
    // DEV-10186: `de` is a RETIRED market. `/de` and `/de-en` 308-redirect to
    // the `eu` market (see `retired-markets.ts`). This row is deliberately kept
    // (retirement means "cannot be chosen or crawled", not "never existed"): a
    // historical order placed under `/de-en` still has to resolve to the `de`
    // market row for its currency/locale, which flows through `marketBaseOf` →
    // this entry. Crawl-facing derivations (sitemap slugs, hreflang, llms.txt)
    // filter it out via `isRetiredMarket` at the point of derivation, so no
    // retired slug reaches a crawler even though the resolution row survives.
    market: "de",
    language: "en",
    hreflang: "en-DE",
    label: "Germany (English)",
  },
  {
    // DEV-10186: German is a LANGUAGE of the `eu` market, not a market of its
    // own. The retired `de` slug redirects here (see `retired-markets.ts`), so
    // this entry has to exist before that redirect can resolve to anything.
    //
    // hreflang `de-eu`, not `de-DE`: this page serves German speakers across the
    // EU market, not Germany specifically. `eu` is not an ISO region. It is
    // the same pseudo-region the store's MARKET_HREFLANG_MAP already uses for
    // `en-eu`, kept consistent here rather than inventing a second convention.
    market: "eu",
    language: "de",
    hreflang: "de-eu",
    label: "Europe (Deutsch)",
  },
  {
    // DEV-10460 follow-up: German is also a language of the `ch` market. The
    // offer backend already serves German content for `ch` (language_in_market
    // row confirmed), this entry is the code-level projection. Registering it
    // makes `/ch-de` routable and lets the language redirect send a German
    // browser in Switzerland to `/ch-de` instead of leaving it on English
    // `/ch`. hreflang `de-CH` is a real ISO region tag, unlike `de-eu` above.
    market: "ch",
    language: "de",
    hreflang: "de-CH",
    label: "Switzerland (Deutsch)",
  },
]

/** Per-market default language ("is_main"). Markets absent here default to en. */
const MARKET_DEFAULT_LANGUAGE: Record<string, Language> = {
  de: "de",
}

const VARIANT_BY_SLUG = new Map(
  MARKET_LANGUAGE_VARIANTS.map((v) => [`${v.market}-${v.language}`, v])
)

export const defaultLanguageFor = (market: string): Language =>
  MARKET_DEFAULT_LANGUAGE[market] ?? DEFAULT_LANGUAGE

/**
 * Decompose a public market slug. Total: any input yields a market string
 * and a supported Language. A registered compound ("de-en") splits into its
 * base market and variant language; anything else is treated as a base
 * market rendering its default language.
 */
export const parseMarketSlug = (
  slug: string | null | undefined
): { market: string; language: Language } => {
  const normalized = slug?.trim().toLowerCase() ?? ""
  const variant = VARIANT_BY_SLUG.get(normalized)
  if (variant) {
    return { market: variant.market, language: variant.language }
  }
  return { market: normalized, language: defaultLanguageFor(normalized) }
}

/** Compose the public slug: bare market for its default, compound otherwise. */
export const composeMarketSlug = (
  market: string,
  language: Language
): string =>
  language === defaultLanguageFor(market) ? market : `${market}-${language}`

/** Base market of any public slug: the ONLY form the backend may see. */
export const marketBaseOf = (slug: string | null | undefined): string =>
  parseMarketSlug(slug).market

/**
 * Rewrites a backend-built, market-prefixed path so it stays in the visitor's
 * market-language variant. Backend URLs are baked with the base market (it
 * only knows base markets, DEV-9850); on a variant slug like "de-en" the
 * leading "/de/" segment must be rewritten to "/de-en/". No-op for base slugs
 * and for URLs not prefixed with the slug's base market.
 */
export const localizeMarketPath = (
  path: string,
  marketSlug: string
): string => {
  const base = marketBaseOf(marketSlug)
  if (base === marketSlug) return path
  // The base prefix must be a full path segment: followed by "/", end of
  // string, a query string, or a fragment ("/de?ref=x" counts, "/decor" does not).
  const after = path.slice(base.length + 1)
  return path.startsWith(`/${base}`) &&
    (after === "" ||
      after.startsWith("/") ||
      after.startsWith("?") ||
      after.startsWith("#"))
    ? `/${marketSlug}${after}`
    : path
}

/** Registered variant slugs for a market (e.g. "de" → ["de-en"]). */
export const variantSlugsFor = (market: string): string[] =>
  MARKET_LANGUAGE_VARIANTS.filter((v) => v.market === market).map((v) =>
    composeMarketSlug(v.market, v.language)
  )

/**
 * The languages a market serves, default first, then its registered variant
 * languages (deduped). Single-language markets return `[defaultLanguageFor(m)]`.
 * Registry-driven: adding a market to MARKET_LANGUAGE_VARIANTS extends this
 * automatically with no code change here.
 */
export const languagesForMarket = (market: string): Language[] => {
  const base = defaultLanguageFor(market)
  const variants = MARKET_LANGUAGE_VARIANTS.filter(
    (v) => v.market === market
  ).map((v) => v.language)
  return [base, ...variants.filter((l) => l !== base)]
}

/**
 * Pilot market → language resolution. Where the fetched market object is
 * available (RSC pages, post-DEV-9526) prefer `languageOf(market.language)`
 * directly; this slug-based map is the fallback for call sites that only have a
 * market slug. Pilot: the `de` market speaks German; everything else is
 * English, now parse-backed to account for registered market variants.
 */
export const getLanguageForMarket = (
  marketSlug: string | null | undefined
): Language => {
  if (!marketSlug) return DEFAULT_LANGUAGE
  return parseMarketSlug(marketSlug).language
}

/**
 * BCP-47 tag for a language, used to seed `Intl.*` formatters. The pilot maps
 * German to the full regional tag `de-DE` (correct grouping/decimal separators
 * and currency-symbol placement); English stays the bare `en` so `Intl` keeps
 * its neutral English behaviour.
 *
 * WARNING: HIGHEST-RISK SILENT GAP IN THE REPO. Widening `SUPPORTED_LANGUAGES` does
 * NOT fail the build here. A language missing from this ternary just formats
 * as English. Every price, date, number and plural is seeded from this one
 * line (`format.ts` builds `Intl.PluralRules` from it, and `t.ts` resolves
 * catalog plural keys through the same cache), so a miss ships wrong decimal
 * separators, wrong date order and the wrong plural branch with nothing logged
 * and nothing failing. Edit this in the SAME commit that widens the union,
 * never separately. See `services` repo `docs/i18n/language-change-surface.md`.
 */
export const bcp47 = (language: Language): string =>
  language === "de" ? "de-DE" : "en"
